import { differenceInDays } from 'date-fns';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button, Column, Divider, Row, Text } from '~/commonComponents';
import { JobPostingStatus, useGetOfficePlanDetail } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { WarningSolidIcon } from '~/images/icons';
import { routes } from '~/routes';
import { Colors } from '~/themes/colors';

interface ISuspendedBannerProps {
  jobStatus: JobPostingStatus;
  officeId?: string;
  officePlanId?: string;
}

export const SuspendedBanner = ({ jobStatus, officeId, officePlanId }: ISuspendedBannerProps) => {
  const navigate = useNavigate();

  // Fetch office plan details to get grace period information
  const {
    data: officePlanData,
    isError,
    error,
  } = useGetOfficePlanDetail(officePlanId, officeId, {
    enabled: !!officePlanId && jobStatus === JobPostingStatus.Suspended,
  });

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error]);

  // Calculate remaining days until grace period ends
  const remainingDays = officePlanData?.gracePeriodEndsAt
    ? differenceInDays(new Date(officePlanData.gracePeriodEndsAt), new Date())
    : null;

  if (jobStatus !== JobPostingStatus.Suspended) {
    return null;
  }

  return (
    <Row
      alignItems="center"
      backgroundColor={Colors.warning_30}
      borderColor={Colors.warning_700}
      borderRadius={6}
      borderStyle="solid"
      borderWidth={1}
      gap={12}
      marginTop={20}
      paddingBlock={12}
      paddingInline={10}
    >
      <WarningSolidIcon color={Colors.warning_500} size={24} />
      <Divider height="95%" width={1} color={Colors.warning_500} />
      <Row flex={1}>
        <Column>
          <Text bold color={Colors.warning_700} marginBottom={4}>
            This Job Posting Is Suspended:
          </Text>
          <Text color={Colors.warning_700}>
            Candidates can no longer view or apply to this job. Onsite interviews within{' '}
            {officePlanData?.gracePeriodDays ?? '-'} days of the suspension date will remain active.
            All others have been canceled. You can reactivate this posting by updating your payment method within the next{' '}
            {remainingDays ?? '-'} days before it&apos;s permanently closed.
          </Text>
        </Column>
        <Column flex={1} alignSelf="center" alignItems="center">
          <Button
            text="Update Payment"
            textStyle={{ fontSize: 16, color: Colors.white }}
            style={{
              width: 148,
              height: 30,
              marginLeft: 32,
              marginRight: 48,
              backgroundColor: Colors.warning_500,
            }}
            onClick={() => navigate(routes.paymentInformation)}
          />
        </Column>
      </Row>
    </Row>
  );
};
